import { Component, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'ngx-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {

  profileData: Record<string, any> = {};
  savedProfileData: Record<string, any> = {};

  isLoading = true;
  isChangePasswordPopupOpened = false;
  isDataChanged = false;
  isContentScrolled = false;
  isEditMode = false;

  // ✅ NEW
  companyName = '-';
  profileCompletion = 0;        // final %
  animatedCompletion = 0;       // UI animated %

  supervisorsList: string[] = ['Alice Johnson', 'Michael Smith', 'Sarah Lee'];
canEditRoleDepartment = false;
  currentUserRole: any;
  currentUserDepartment: any;

  constructor(
    public authProfileService: AuthService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  ngOnInit() {


    this.authProfileService.getCurrentUserProfile().subscribe(async data => {
      if (data) {
        const fullName = (data.fullName || data.name || '').trim();
        const parts = fullName.split(' ').filter(Boolean);

        this.profileData = {
          ...data,
          firstName: parts[0] || '',
          lastName: parts.slice(1).join(' ') || '',
          profileImage: data.profileImage || data.image || null,
          position: data.role || '',
        };
    this.currentUserRole = data.role || '';
    this.currentUserDepartment = data.department || '';
        this.canEditRoleDepartment =
      this.currentUserRole === 'super-admin' ||
      this.currentUserDepartment?.toLowerCase() === 'hr';

        await this.loadCompanyName(this.profileData.companyId);

        this.setSavedData();

        this.calculateCompletion(true);
      }

      this.isLoading = false;
    });
  }







  /* -----------------------------
    ✅ COMPANY NAME
  ----------------------------- */
  async loadCompanyName(companyId: string) {
    if (!companyId) { this.companyName = '-'; return; }

    try {
      const company = await this.authProfileService.getCompanyById(companyId);
      this.companyName = company?.name || '-';
    } catch {
      this.companyName = '-';
    }
  }

  /* -----------------------------
    ✅ AVATAR INITIALS FALLBACK
  ----------------------------- */
  get initials(): string {
    const fn = (this.profileData?.firstName || '').trim();
    const ln = (this.profileData?.lastName || '').trim();
    const a = fn ? fn[0] : '';
    const b = ln ? ln[0] : '';
    const init = (a + b).toUpperCase();
    return init || (this.profileData?.email ? this.profileData.email[0].toUpperCase() : 'U');
  }

  /* -----------------------------
    ✅ COMPLETION % + ANIMATION
  ----------------------------- */
  calculateCompletion(animate = false) {
    const fields = [
      'firstName',
      'lastName',
      'phone',
      'department',
      'position',
      'address',
      'city',
      'country',
      'image',
    ];

    const filled = fields.filter(f => !!(this.profileData?.[f] ?? '')).length;
    const next = Math.round((filled / fields.length) * 100);

    this.profileCompletion = next;

    if (animate) this.animateProgress(this.animatedCompletion, next);
    else this.animatedCompletion = next;
  }

  animateProgress(from: number, to: number, duration = 500) {
    const start = performance.now();
    const diff = to - from;

    const step = (t: number) => {
      const p = Math.min((t - start) / duration, 1);
      this.animatedCompletion = Math.round(from + diff * p);
      if (p < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }

  /* -----------------------------
    UI helpers
  ----------------------------- */
  dataChanged() {
    this.isDataChanged = true;
  }

  setSavedData(data = this.profileData) {
    this.savedProfileData = JSON.parse(JSON.stringify(data));
  }

  copyToClipboard(text: string) {
    navigator.clipboard?.writeText(text);
  }

  changePassword() {
    this.isChangePasswordPopupOpened = true;
  }

  editProfile() {
    this.isEditMode = true;
  }

  cancel() {
    this.profileData = JSON.parse(JSON.stringify(this.savedProfileData));
    this.setSavedData();
    this.isEditMode = false;
    this.isDataChanged = false;
    this.calculateCompletion(true);
  }

  /* -----------------------------
    ✅ PHOTO UPLOAD + COMPRESS
  ----------------------------- */
async onProfilePhotoSelected(event: any) {
  const file = event.target.files?.[0];
  if (!file) return;

  const arrayBuffer = await file.arrayBuffer();
  const orientation = this.getJpegOrientation(arrayBuffer);

  const reader = new FileReader();
  reader.onload = async () => {
    const base64 = reader.result as string;

    // ✅ compress + fix orientation
    const fixed = await this.compressAndFixOrientation(base64, orientation, 600, 600, 0.75);

    // history (only if changed)
    if (this.profileData?.uid) {
      const prev = this.savedProfileData?.profileImage || this.savedProfileData?.image || null;

    }

    this.profileData.image = fixed;
    this.dataChanged();
    this.calculateCompletion(true);
  };
  reader.readAsDataURL(file);
}
// ✅ Read EXIF orientation (JPEG only) - lightweight
getJpegOrientation(buffer: ArrayBuffer): number {
  const view = new DataView(buffer);
  if (view.getUint16(0, false) !== 0xFFD8) return 1; // not jpeg

  let offset = 2;
  while (offset < view.byteLength) {
    const marker = view.getUint16(offset, false);
    offset += 2;
    if (marker === 0xFFE1) { // EXIF
      const exifLength = view.getUint16(offset, false);
      offset += 2;

      const exifHeader = view.getUint32(offset, false);
      if (exifHeader !== 0x45786966) return 1; // "Exif"
      offset += 6; // "Exif\0\0"

      const little = view.getUint16(offset, false) === 0x4949;
      offset += view.getUint32(offset + 4, little);

      const tags = view.getUint16(offset, little);
      offset += 2;

      for (let i = 0; i < tags; i++) {
        const tagOffset = offset + i * 12;
        const tag = view.getUint16(tagOffset, little);
        if (tag === 0x0112) { // Orientation
          return view.getUint16(tagOffset + 8, little);
        }
      }
      return 1;
    } else {
      offset += view.getUint16(offset, false);
    }
  }
  return 1;
}
async downloadImage() {
  if (!this.previewImageSrc) return;

  const watermarkText = `${this.profileData?.name || this.profileData?.fullName || 'Portus'} • ${new Date().toLocaleDateString()}`;

  const img = new Image();
  img.src = this.previewImageSrc;
  await img.decode();

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);

  // watermark bottom-right
  ctx.font = `${Math.max(18, Math.floor(canvas.width * 0.02))}px Arial`;
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 3;

  const pad = Math.max(18, Math.floor(canvas.width * 0.02));
  const textW = ctx.measureText(watermarkText).width;

  const x = canvas.width - textW - pad;
  const y = canvas.height - pad;

  ctx.strokeText(watermarkText, x, y);
  ctx.fillText(watermarkText, x, y);

  const a = document.createElement('a');
  a.href = canvas.toDataURL('image/jpeg', 0.92);
  a.download = 'profile-image-watermarked.jpg';
  a.click();
}

compressAndFixOrientation(
  base64: string,
  orientation: number,
  maxW = 600,
  maxH = 600,
  quality = 0.75
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let w = img.width, h = img.height;

      // resize
      const ratio = Math.min(maxW / w, maxH / h, 1);
      w = Math.round(w * ratio);
      h = Math.round(h * ratio);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      // handle rotations / flips based on EXIF orientation
      const swap = [5,6,7,8].includes(orientation);
      canvas.width = swap ? h : w;
      canvas.height = swap ? w : h;

      ctx.save();
      this.applyOrientationTransform(ctx, orientation, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      ctx.restore();

      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = base64;
  });
}

applyOrientationTransform(ctx: CanvasRenderingContext2D, o: number, w: number, h: number) {
  switch (o) {
    case 2: ctx.translate(w, 0); ctx.scale(-1, 1); break;              // mirror X
    case 3: ctx.translate(w, h); ctx.rotate(Math.PI); break;           // 180
    case 4: ctx.translate(0, h); ctx.scale(1, -1); break;              // mirror Y
    case 5: ctx.rotate(0.5 * Math.PI); ctx.scale(1, -1); break;        // 90 + mirror Y
    case 6: ctx.translate(h, 0); ctx.rotate(0.5 * Math.PI); break;     // 90
    case 7: ctx.translate(h, 0); ctx.rotate(0.5 * Math.PI); ctx.scale(-1, 1); break;
    case 8: ctx.translate(0, w); ctx.rotate(-0.5 * Math.PI); break;    // -90
    default: break;
  }
}


  compressImage(base64: string, maxWidth = 400, maxHeight = 400, quality = 0.7): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
        } else {
          if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = base64;
    });
  }

  /* -----------------------------
    ✅ SAVE + AUDIT LOG
    🔒 Email locked forever
  ----------------------------- */
  async save() {
    try {
      const fullName = `${this.profileData.firstName || ''} ${this.profileData.lastName || ''}`.trim();

      const payload: any = {
        ...this.profileData,
        fullName,
        name: fullName,
        profileImage: this.profileData.image,
        updatedAt: new Date(),
      };

      // 🔒 email never changes
      payload.email = this.savedProfileData.email;

      delete payload.firstName;
      delete payload.lastName;
      delete payload.image;

      const before = JSON.parse(JSON.stringify(this.savedProfileData));
      const after = JSON.parse(JSON.stringify(payload));

      await this.authProfileService.saveUserProfile(after);

      await this.authProfileService.createAuditLog({
        uid: payload.uid,
        action: 'PROFILE_UPDATED',
        before,
        after,
        timestamp: new Date(),
      });

      // refresh UI
      this.profileData = {
        ...payload,
        firstName: (fullName.split(' ')[0] || ''),
        lastName: (fullName.split(' ').slice(1).join(' ') || ''),
        profileImage: payload.profileImage || null,
      };

      await this.loadCompanyName(this.profileData.companyId);

      this.isEditMode = false;
      this.isDataChanged = false;
      this.setSavedData(this.profileData);
      this.calculateCompletion(true);

    } catch (err) {
      console.error('Error saving profile:', err);
    }
  }

  scroll(event: any) {
    this.isContentScrolled = event.target.scrollTop > 0;
  }
  // ===============================
// ADVANCED VIEWER: zoom + pan + pinch
// ===============================
isImagePreviewOpen = false;
previewImageSrc: string | null = null;

zoomLevel = 1;
rotation = 0;
panX = 0;
panY = 0;

private isPanning = false;
private panStartX = 0;
private panStartY = 0;

private pinchStartDist = 0;
private pinchStartZoom = 1;
private lastTap = 0;

// open/close
openImagePreview(src: string) {
  if (!src) return;
  this.previewImageSrc = src;
  this.isImagePreviewOpen = true;

  // reset view
  this.zoomLevel = 1;
  this.rotation = 0;
  this.panX = 0;
  this.panY = 0;

  document.body.style.overflow = 'hidden';
}

closeImagePreview() {
  this.isImagePreviewOpen = false;
  this.previewImageSrc = null;
  document.body.style.overflow = '';
}

// ESC close
@HostListener('window:keydown', ['$event'])
handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' && this.isImagePreviewOpen) {
    this.closeImagePreview();
  }
}

// wheel zoom
onWheelZoom(event: WheelEvent) {
  event.preventDefault();
  const delta = event.deltaY < 0 ? 0.12 : -0.12;
  this.zoomLevel = this.clamp(this.zoomLevel + delta, 0.5, 5);
  if (this.zoomLevel <= 1) { this.panX = 0; this.panY = 0; }
}

// double tap zoom
onTouchEnd() {
  const now = Date.now();
  if (now - this.lastTap < 280) {
    if (this.zoomLevel > 1) {
      this.zoomLevel = 1; this.panX = 0; this.panY = 0;
    } else {
      this.zoomLevel = 2;
    }
  }
  this.lastTap = now;
}

// zoom buttons
zoomIn() { this.zoomLevel = this.clamp(this.zoomLevel + 0.25, 0.5, 5); }
zoomOut() {
  this.zoomLevel = this.clamp(this.zoomLevel - 0.25, 0.5, 5);
  if (this.zoomLevel <= 1) { this.panX = 0; this.panY = 0; }
}
rotate() { this.rotation = (this.rotation + 90) % 360; }

// -------------------------------
// ✅ PAN (drag) - mouse + touch (pointer events)
// -------------------------------
onPointerDown(e: PointerEvent) {
  if (this.zoomLevel <= 1) return;
  this.isPanning = true;
  this.panStartX = e.clientX - this.panX;
  this.panStartY = e.clientY - this.panY;
}

onPointerMove(e: PointerEvent) {
  if (!this.isPanning) return;
  this.panX = e.clientX - this.panStartX;
  this.panY = e.clientY - this.panStartY;
}

onPointerUp() {
  this.isPanning = false;
}

// -------------------------------
// ✅ PINCH ZOOM (two finger)
// -------------------------------
onTouchStart(e: TouchEvent) {
  if (e.touches.length === 2) {
    this.pinchStartDist = this.getTouchDist(e.touches[0], e.touches[1]);
    this.pinchStartZoom = this.zoomLevel;
  }
}

onTouchMove(e: TouchEvent) {
  if (e.touches.length === 2) {
    e.preventDefault();
    const dist = this.getTouchDist(e.touches[0], e.touches[1]);
    const ratio = dist / (this.pinchStartDist || dist);
    this.zoomLevel = this.clamp(this.pinchStartZoom * ratio, 0.5, 5);

    if (this.zoomLevel <= 1) { this.panX = 0; this.panY = 0; }
  }
}

private getTouchDist(t1: Touch, t2: Touch) {
  const dx = t2.clientX - t1.clientX;
  const dy = t2.clientY - t1.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

private clamp(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), max);
}

}
